import { put, call, takeEvery, all, takeLatest, fork } from 'redux-saga/effects';
import * as types from './actionTypes.projectAnalytics';
import * as errorSaga from '../errors/saga.errors';
import _ from 'lodash';
import moment from 'moment';
import store from '../../store';
import deployments from '../../const/deployments';
import Api from '../../api/Api';
import { insideAtlanticOcean } from '../../services/mini/insidePolygon';
import { getRecognizedInteractionsStats, getSessionStats, getUserSessionsStats } from '../../services/mini/analytics/getStatsForCharts';
import { getAnalyticsReportDates } from '../../services/mini/analytics/getAnalyticsReportDates';
import { AnalyticsChartPeriods } from '../../services/mini/analytics/analyticsChartPeriods';
import guid from '../../services/mini/guid';
import { getHistoryGranularityMsParam } from '../../services/mini/getHistoryGranularityMsParam';
import { PHRASE_FILTER, PATTERNHASH_FILTER, PATTERN_FILTER, ENTITY_VALUE_FILTER, ENTITY_TYPE_FILTER, DIALOG_FILTER } from '../../const/logFilters';
import logLevels from '../../const/logLevels';
import nextEntityNameGenerator from '../../services/mini/nextEntityNameGenerator';
import { HOUR } from '../../const/timeRanges';

const DEFAULT_REPORT_NAME = 'Report 1';

export function* getAnalyticsReports({ project, environment, resolve, reject }) {
    try {
        const projectId = project.projectId;
        let analyticsReportsMetaData = yield call(Api.loadAnalyticsReportsMetaData, projectId);
        if (analyticsReportsMetaData.reports.length === 0) {
            yield addAnalyticsReport({
                project,
                environment,
                report: {
                    name: DEFAULT_REPORT_NAME
                }
            });
            analyticsReportsMetaData = yield call(Api.loadAnalyticsReportsMetaData, projectId);
        }
        let { reports, settings } = analyticsReportsMetaData;
        yield put({
            type: types.GET_ANALYTICS_REPORTS_OK,
            projectId,
            reports,
            settings
        });
        for (let i = 0; i < reports.length; i++) {
            yield getDataByReport({ projectId, environment, reports, report: reports[i] });
        }
        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* getDataByReport({ projectId, environment, report, resolve, reject }) {
    try {
        const deploy = deployments[environment];
        const { startTs, endTs } = getAnalyticsReportDates(report.startTs, report.endTs, report.period);

        yield fork(forkChartsData, { projectId, report, deploy, startTs, endTs });
        yield fork(forkUsersAndDialogsData, { projectId, report, environment, startTs, endTs });
        yield fork(forkPatternFlowStatisticsByDates, { projectId, report, environment, startTs, endTs, dialogIds: [], onlyPatternOnPatternFlow: report.onlyPatternOnPatternFlow });
        yield fork(forkPatternStatisticsByDates, { projectId, report, environment, startTs, endTs, dialogIds: [] });
        yield fork(forkIntentsForAnalytics, { projectId, report, environment, startTs, endTs, dialogIds: [] });
        yield fork(forkLocationData, { projectId, report, deploy, startTs, endTs });

        if (resolve) {
            resolve();
        }
    } catch (error) {
        yield errorSaga.putError(error);
        if (reject) {
            reject();
        }
    }
}

export function* forkChartsData({ projectId, report, deploy, startTs, endTs }) {
    const reportName = report.name;
    let historyGranularityMs = getHistoryGranularityMsParam(report.period);

    const stats = yield call(Api.getProjectStatisticsByRange, projectId, deploy, startTs, endTs, historyGranularityMs);

    if (stats.history) {
        stats.history = stats.history.map(el => {
            return { ...el, timeTs: +el.timeTs };
        });
        stats.history = _.sortBy(stats.history, 'timeTs');
    }

    let micSessionStats = getSessionStats(stats, report.period);
    let voiceInteractionsStats = getRecognizedInteractionsStats(stats, report.period);
    let userSessionsStats = getUserSessionsStats(stats, report.period);

    yield fillPartialReportData({
        projectId,
        reportName,
        data: {
            stats,
            micSessionStats,
            voiceInteractionsStats,
            userSessionsStats,
            chartsDataUpdateTs: Date.now()
        }
    });
}

export function* forkUsersAndDialogsData({ projectId, environment, report, startTs, endTs }) {
    const reportName = report.name;
    const { users, dialogs } = yield getProjectUsersAndDialogsStatisticsByDates({ projectId, environment, startTs, endTs });

    yield fillPartialReportData({
        projectId,
        reportName,
        data: {
            usersAndDialogsDataUpdateTs: Date.now(),
            users,
            dialogs
        }
    });
}

export function* forkPatternFlowStatisticsByDates({ projectId, environment, report, startTs, endTs, dialogIds, onlyPatternOnPatternFlow }) {
    // const reportName = report.name;
    // const { patternFlows } = yield getPatternFlowStatisticsByDates({ projectId, environment, startTs, endTs, dialogIds, onlyPatternOnPatternFlow });

    // yield fillPartialReportData({
    //     projectId,
    //     reportName,
    //     data: {
    //         patternFlows,
    //         patternFlowsDataUpdateTs: Date.now()
    //     }
    // });
}

export function* forkPatternStatisticsByDates({ projectId, report, environment, startTs, endTs, dialogIds }) {
    const reportName = report.name;
    const { patterns } = yield getPatternStatisticsByDates({ projectId, environment, startTs, endTs, dialogIds });

    yield fillPartialReportData({
        projectId,
        reportName,
        data: {
            patterns,
            patternsDataUpdateTs: Date.now()
        }
    });
}

export function* forkIntentsForAnalytics({ projectId, report, environment, startTs, endTs, dialogIds }) {
    const reportName = report.name;
    const { intents } = yield getIntentsForAnalytics({ projectId, environment, startTs, endTs, dialogIds });

    yield fillPartialReportData({
        projectId,
        reportName,
        data: {
            intentsDataUpdateTs: Date.now(),
            intents
        }
    });
}

export function* forkLocationData({ projectId, report, deploy, startTs, endTs }) {
    const reportName = report.name;
    const locationData = yield call(Api.getProjectLocationStatisticsByDates, projectId, deploy, startTs, endTs);
    let { stats: locationStats, userCount } = locationData;

    if (locationStats && locationStats.length > 0) {
        locationStats = locationStats.filter(stat => stat.latitude !== 0 && stat.longitude !== 0 && !insideAtlanticOcean([stat.latitude, stat.longitude]));
    }

    yield fillPartialReportData({
        projectId,
        reportName,
        data: {
            locationStatsDataUpdateTs: Date.now(),
            locationStats: {
                data: locationStats,
                userCount,
            },
        }
    });
}

export function* fillPartialReportData({ projectId, reportName, data }) {
    try {
        yield put({
            type: types.GET_PARTIAL_DATA_BY_REPORT_OK,
            projectId,
            reportName,
            data
        });
    }
    catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* updateReportDataByDialogs({ projectId, filters, environment, reports, report, dialogIds, resolve, reject }) {
    try {
        let existingReports = reports || getReportsFromStore(projectId);
        const reportIndex = _.findIndex(existingReports, { name: report.name });
        const { startTs, endTs } = getAnalyticsReportDates(report.startTs, report.endTs, report.period);

        yield put({
            type: types.UPDATE_PATTERNS_IN_ANALYTICS_REPORT_OK,
            projectId,
            reportName: report.name,
            reports: existingReports,
            data: {
                ...existingReports[reportIndex].data,
                patterns: null,
                patternFlows: null,
                intents: null,
                intentsDataUpdateTs: Date.now(),
                patternsDataUpdateTs: Date.now(),
                patternFlowsDataUpdateTs: Date.now()
            },
        });

        let { patterns } = yield getPatternStatisticsByDates({ projectId, environment, startTs, endTs, dialogIds });
        // let { patternFlows } = yield getPatternFlowStatisticsByDates({ projectId, environment, startTs, endTs, dialogIds });
        let { intents } = yield getIntentsForAnalytics({ projectId, environment, startTs, endTs, dialogIds, filters });
        yield put({
            type: types.UPDATE_PATTERNS_IN_ANALYTICS_REPORT_OK,
            projectId,
            reportName: report.name,
            reports: existingReports,
            data: {
                ...existingReports[reportIndex].data,
                patterns,
                // patternFlows,
                intents,
                intentsDataUpdateTs: Date.now(),
                patternsDataUpdateTs: Date.now(),
                patternFlowsDataUpdateTs: Date.now()
            },
        });

        if (resolve) {
            resolve();
        }
    } catch (error) {
        yield errorSaga.putError(error);
        if (reject) {
            reject();
        }
    }
}

export function* updateIntentsInDataReport({ projectId, environment, reports, report, filters, dialogIds, resolve, reject }) {
    try {
        let existingReports = reports || getReportsFromStore(projectId);
        const reportIndex = _.findIndex(existingReports, { name: report.name });
        const { startTs, endTs } = getAnalyticsReportDates(report.startTs, report.endTs, report.period);
        yield put({
            type: types.UPDATE_INTENTS_IN_ANALYTICS_REPORT_OK,
            projectId,
            reportName: report.name,
            data: {
                ...existingReports[reportIndex].data,
                intents: null,
                intentsDataUpdateTs: Date.now(),
            },
        });

        let { intents } = yield getIntentsForAnalytics({ projectId, environment, startTs, endTs, filters, dialogIds });
        yield put({
            type: types.UPDATE_INTENTS_IN_ANALYTICS_REPORT_OK,
            projectId,
            reportName: report.name,
            data: {
                ...existingReports[reportIndex].data,
                intents,
                intentsDataUpdateTs: Date.now(),
            },
        });

        if (resolve) {
            resolve();
        }
    } catch (error) {
        yield errorSaga.putError(error);
        if (reject) {
            reject();
        }
    }
}

export function* addAnalyticsReport({ project, environment, report = {}, resolve, reject }) {
    try {
        const projectId = project.projectId;
        let reports = getReportsFromStore(projectId);
        const lastReport = reports[reports.length - 1];
        let newReport;

        if (lastReport) {
            newReport = {
                ...report,
                name: report.name || (lastReport ? nextEntityNameGenerator(lastReport.name) : DEFAULT_REPORT_NAME),
                filters: [...lastReport.filters],
                period: lastReport.period,
                startTs: lastReport.startTs,
                endTs: lastReport.endTs,
                onlyPatternOnPatternFlow: lastReport.onlyPatternOnPatternFlow
            };
        } else {
            newReport = {
                ...report,
                name: DEFAULT_REPORT_NAME,
                filters: [],
                period: AnalyticsChartPeriods.Daily,
                startTs: +moment(+project.createTs).startOf('day'),
                endTs: +moment().endOf('day'),
                onlyPatternOnPatternFlow: true
            };
        }

        reports.push(newReport);
        yield put({ type: types.ADD_ANALYTICS_REPORT_OK, projectId, report: newReport });
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports);
        yield getDataByReport({ projectId, environment, reports, report: newReport });
        if (resolve) {
            resolve();
        }
    } catch (error) {
        yield errorSaga.putError(error);
        if (reject) {
            reject();
        }
    }
}

export function* renameAnalyticsReport({ project, reportName, newReportName, resolve, reject }) {
    try {
        const projectId = project.projectId;
        let reports = getReportsFromStore(projectId);
        const reportIndex = _.findIndex(reports, { name: reportName });
        reports[reportIndex] = { ...reports[reportIndex], name: newReportName };
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports);
        yield put({
            type: types.RENAME_ANALYTICS_REPORT_OK,
            projectId,
            reportName,
            newReportName
        });
        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
        reject();
    }
}

function getReportsFromStore(projectId) {
    let reports = store.getState().projectAnalytics.projectsAnalytics[projectId]?.reports || [];

    return [...reports];
}

export function* deleteAnalyticsReport({ project, reportName, resolve, reject }) {
    try {
        const projectId = project.projectId;
        let reports = getReportsFromStore(projectId);
        const reportIndex = _.findIndex(reports, { name: reportName });
        reports.splice(reportIndex, 1);
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports);
        yield put({ type: types.DELETE_ANALYTICS_REPORT_OK, projectId, reportName });
        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
        reject();
    }
}

export function* addAnalyticsReportFilter({ projectId, reportName, filters, filterIndex, resolve, reject }) {
    try {
        let reports = getReportsFromStore(projectId);
        const reportIndex = _.findIndex(reports, { name: reportName });

        let newFilters = [];
        let currentFilters = reports[reportIndex].filters ?
            [...reports[reportIndex].filters] :
            [];

        if (filterIndex === undefined) {
            newFilters = [
                ...currentFilters,
                ...filters
            ];
        } else {
            newFilters = [...currentFilters];

            newFilters[filterIndex] = filters[0];
        }

        reports[reportIndex] = {
            ...reports[reportIndex], filters: newFilters
        };
        yield put({ type: types.GET_ANALYTICS_REPORTS_OK, projectId, reports });
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports);
        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
        reject();
    }
}


export function* deleteAnalyticsReportFilter({ projectId, reportName, filterIndex, resolve, reject }) {
    try {
        let reports = getReportsFromStore(projectId);
        const reportIndex = _.findIndex(reports, { name: reportName });

        let newFilters = [...reports[reportIndex].filters];
        newFilters.splice(filterIndex, 1);
        reports[reportIndex] = {
            ...reports[reportIndex], filters: newFilters
        };
        yield put({ type: types.GET_ANALYTICS_REPORTS_OK, projectId, reports });
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports);
        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
        reject();
    }
}

export function* changeAnalyticsPeriodReportFilter({ projectId, environment, reportName, period, startTs, endTs, resolve, reject }) {
    try {
        let reports = getReportsFromStore(projectId);
        const reportIndex = _.findIndex(reports, { name: reportName });
        let dates = {};

        if (startTs && endTs) {
            dates = {
                startTs, endTs
            };
        }

        reports[reportIndex] = {
            ...reports[reportIndex],
            period,
            ...dates
        };

        yield put({
            type: types.CHANGE_ANALYTICS_PERIOD_REPORT_FILTER_OK,
            projectId,
            reportName,
            period,
            dates,
            reports,
            data: {}
        });
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports);
        yield getDataByReport({ projectId, environment, reports, report: reports[reportIndex] });
        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
        reject();
    }
}

export function* changeAnalyticsDateRangeReportFilter({ projectId, reportName, environment, startTs, endTs, resolve, reject }) {
    try {
        let reports = getReportsFromStore(projectId);
        const reportIndex = _.findIndex(reports, { name: reportName });

        reports[reportIndex] = {
            ...reports[reportIndex], startTs, endTs, data: {}
        };
        yield put({
            type: types.CHANGE_ANALYTICS_DATE_RANGE_REPORT_FILTER_OK,
            projectId,
            reportName,
            startTs,
            endTs,
            data: {}
        });
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports);
        yield getDataByReport({ projectId, environment, reports, report: reports[reportIndex] });
        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
        reject();
    }
}

export function* toggleShowEventsFilterForPatternFlow({ projectId, reportName, environment, onlyPatternOnPatternFlow, dialogIds, resolve, reject }) {
    try {
        let reports = getReportsFromStore(projectId);
        const reportIndex = _.findIndex(reports, { name: reportName });
        const report = reports[reportIndex];
        const { startTs, endTs } = getAnalyticsReportDates(report.startTs, report.endTs, report.period);

        reports[reportIndex] = {
            ...reports[reportIndex], onlyPatternOnPatternFlow, data: {
                ...reports[reportIndex].data,
                patternFlows: null,
                patternFlowsDataUpdateTs: Date.now()
            }
        };
        yield put({
            type: types.TOGGLE_SHOW_EVENTS_FILTER_FOR_PATTERN_FLOW_OK,
            projectId,
            reportName,
            onlyPatternOnPatternFlow,
            data: {
                ...reports[reportIndex].data,
                patternFlows: null,
                patternFlowsDataUpdateTs: Date.now()
            }
        });
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports);

        // const { patternFlows } = yield getPatternFlowStatisticsByDates({ projectId, environment, startTs, endTs, dialogIds, onlyPatternOnPatternFlow });

        yield put({
            type: types.TOGGLE_SHOW_EVENTS_FILTER_FOR_PATTERN_FLOW_OK,
            projectId,
            reportName,
            onlyPatternOnPatternFlow,
            data: {
                ...reports[reportIndex].data,
                // patternFlows,
                patternFlowsDataUpdateTs: Date.now()
            }
        });
        resolve();
    } catch (error) {
        yield errorSaga.putError(error);
        reject();
    }
}

export function* resetAnalyticsReportsData({
    projectId,
    reportIndex = null,
    dataToReset = {
        stats: null,
        micSessionStats: null,
        voiceInteractionsStats: null,
        userSessionsStats: null,
        locationStats: null,
        users: null,
        dialogs: null,
        patternFlows: null,
        patterns: null,
        intents: null,
        chartsDataUpdateTs: Date.now(),
        intentsDataUpdateTs: Date.now(),
        patternsDataUpdateTs: Date.now(),
        patternFlowsDataUpdateTs: Date.now()
    },
    resolve,
    reject }) {
    try {
        let reports = getReportsFromStore(projectId);

        if (reportIndex === null) {
            for (let i = 0; i < reports.length; i++) {
                const curReport = reports[i];
                reports[i] = {
                    ...curReport,
                    data: {
                        ...curReport.data,
                        ...dataToReset
                    }
                };
            }
        } else {
            const curReport = reports[reportIndex];
            if (curReport) {
                reports[reportIndex] = {
                    ...curReport,
                    data: {
                        ...curReport.data,
                        ...dataToReset
                    }
                };
            }
        }

        yield put({ type: types.GET_ANALYTICS_REPORTS_OK, projectId, reports });
        if (resolve) {
            resolve();
        }
    } catch (error) {
        yield errorSaga.putError(error);
        if (reject) {
            reject();
        }
    }
}

export function* toggleReportSection({ project, sectionName, collapsed, resolve, reject }) {
    try {
        const projectId = project.projectId;
        let reports = getReportsFromStore(projectId);
        let settings = store.getState().projectAnalytics.projectsAnalytics[projectId]?.settings || {};
        settings = {
            ...settings,
            collapsedState: {
                ...settings.collapsedState,
                [sectionName]: collapsed
            }
        };
        yield put({ type: types.TOGGLE_REPORT_SECTION_OK, projectId, settings });
        yield call(Api.saveAnalyticsReportMetaData, projectId, reports, settings);
    } catch (error) {
        yield errorSaga.putError(error);
        reject();
    }
}

export function* getProjectUsersAndDialogsStatisticsByDates({ projectId, environment, startTs, endTs }) {
    try {
        const deploy = deployments[environment];
        const limit = 300;
        const { users } = yield call(Api.getProjectUsersAndDialogsStatisticsByDates, projectId, deploy, startTs, endTs, limit);
        let dialogs = [];

        for (let i = 0; i < users.length; i++) {
            dialogs = dialogs.concat(users[i].dialogs);
        }
        return { users, dialogs };
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* getPatternFlowStatisticsByDates({ projectId, environment, startTs, endTs, dialogIds, onlyPatternOnPatternFlow = true }) {
    // try {
    //     const deploy = deployments[environment];
    //     const onlyPatterns = onlyPatternOnPatternFlow;
    //     const { patternFlows } = yield call(Api.getPatternFlowStatisticsByDates, { projectId, deploy, fromTs: startTs, toTs: endTs, dialogIds, onlyPatterns });

    //     return { patternFlows };
    // } catch (error) {
    //     yield errorSaga.putError(error);
    // }
}


export function* getPatternStatisticsByDates({ projectId, environment, startTs, endTs, dialogIds }) {
    try {
        const deploy = deployments[environment];
        let { patterns } = yield call(Api.getPatternStatisticsByDates, projectId, deploy, startTs, endTs, dialogIds);

        for (let i = 0; i < patterns.length; i++) {
            const curPattern = patterns[i];
            patterns[i].avgScore = (curPattern.minScore + curPattern.maxScore) / 2;
            patterns[i].id = guid();
        }

        patterns = _.sortBy(patterns, 'matches').reverse();
        return { patterns };
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* getIntentsForAnalytics({ projectId, filters = {}, environment, startTs, endTs, dialogIds }) {
    let file = "";
    let deployment = deployments[environment];
    let dialogId = "";
    let level = [];
    let line = -1;
    let type = 2;
    let scriptTagId = "";
    let lastTs = null;
    let scriptsHash = '';
    let phrases = [];
    let phraseFilters = [];
    let filteredByPhrases = false;

    let fromTs = startTs;
    let toTs = endTs;

    level.push(logLevels.runTimeError);
    level.push(logLevels.info);
    level.push(logLevels.debug);

    if (filters[PHRASE_FILTER] != null || filters[PATTERNHASH_FILTER] != null || filters[PATTERN_FILTER] != null || filters[DIALOG_FILTER] != null || filters?.entities?.length > 0) {
        filteredByPhrases = true;
    }

    if (filters[PHRASE_FILTER] != null) {
        phraseFilters.push({ fieldName: PHRASE_FILTER, value: filters[PHRASE_FILTER] });
    }

    if (filters[PATTERNHASH_FILTER] != null) {
        phraseFilters.push({ fieldName: PATTERNHASH_FILTER, value: filters[PATTERNHASH_FILTER] });
    }

    if (filters[PATTERN_FILTER] != null) {
        phraseFilters.push({ fieldName: PATTERN_FILTER, value: filters[PATTERN_FILTER] });
    }

    if (filters[DIALOG_FILTER] != null) {
        dialogId = filters[DIALOG_FILTER];
    }

    if (filters.entities && filters.entities.length > 0) {
        phraseFilters = [...phraseFilters, ...filters.entities.map(entity => {
            const key = Object.keys(entity)[0];
            return { fieldName: key, value: entity[key] };
        })];
    }

    try {
        if ((filters.currentDialog && dialogId) || !filters.currentDialog) {
            phrases = yield call(Api.readPhrases, { projectId, deployment, file, scriptTagId, dialogId, dialogIds, line, type, lastTs, fromTs, toTs, limit: 300, undefined, scriptsHash, filters: phraseFilters });
        }

        for (let i = 0; i < phrases.length; i++) {
            phrases[i].level = logLevels.phrase;
        }

        return { intents: phrases };
    } catch (error) {
        yield errorSaga.putError(error);
    }
}

export function* watch() {
    yield takeEvery(types.GET_ANALYTICS_REPORTS_REQ, getAnalyticsReports);
    yield takeEvery(types.GET_DATA_BY_REPORT_REQ, getDataByReport);
    yield takeEvery(types.UPDATE_PATTERNS_IN_ANALYTICS_REPORT_REQ, updateReportDataByDialogs);
    yield takeEvery(types.UPDATE_INTENTS_IN_ANALYTICS_REPORT_REQ, updateIntentsInDataReport);
    yield takeEvery(types.ADD_ANALYTICS_REPORT_REQ, addAnalyticsReport);
    yield takeEvery(types.RENAME_ANALYTICS_REPORT_REQ, renameAnalyticsReport);
    yield takeEvery(types.ADD_ANALYTICS_REPORT_FILTER_REQ, addAnalyticsReportFilter);
    yield takeEvery(types.DELETE_ANALYTICS_REPORT_FILTER_REQ, deleteAnalyticsReportFilter);
    yield takeLatest(types.CHANGE_ANALYTICS_PERIOD_REPORT_FILTER_REQ, changeAnalyticsPeriodReportFilter);
    yield takeLatest(types.CHANGE_ANALYTICS_DATE_RANGE_REPORT_FILTER_REQ, changeAnalyticsDateRangeReportFilter);
    yield takeLatest(types.TOGGLE_SHOW_EVENTS_FILTER_FOR_PATTERN_FLOW_REQ, toggleShowEventsFilterForPatternFlow);
    yield takeEvery(types.DELETE_ANALYTICS_REPORT_REQ, deleteAnalyticsReport);
    yield takeEvery(types.RESET_ANALYTICS_REPORTS_DATA_REQ, resetAnalyticsReportsData);
    yield takeEvery(types.TOGGLE_REPORT_SECTION_REQ, toggleReportSection);
}